<script lang="ts">
	import Layout from "../components/Layout.svelte";
	import { getTranslate } from "../utils/getTranslate";
	import translations from "./Compliance.translations.json";
	import HeroBanner from "../components/HeroBanner.svelte";
	import complianceMd from "../assets/images/compliance.jpg?webp&w=768&imagetools";
	import compliance from "../assets/images/compliance.jpg?webp&w=1920&imagetools";
	import complianceThumbnail from "../assets/images/compliance.jpg?w=1200&h=630&imagetools";
	import carFooterBanner from "../assets/images/car-footer-banner.png?webp&w=1920&imagetools";
	import { getGlobalSettings } from "../getGlobalSettings";
	import { thumbnailImageContextKey } from "../contexts/thumbnailImageContextKey.js";
	import { setContext } from "svelte";

	const translate = getTranslate(translations);

	const { COOKIES } = getGlobalSettings();

	const complianceHeaders = [
		"codeOfEthics",
		// "gdpr",
		"complianceHotline",
		"internalRegulations",
	] satisfies (keyof typeof translations)[];
	const parenthesesMap: Partial<Record<(typeof complianceHeaders)[number], keyof typeof translations>> = {
		complianceHotline: "complianceHotlineParentheses",
	};

	const reports = [
		"complianceHotlineReport1",
		"complianceHotlineReport2",
		"complianceHotlineReport3",
		"complianceHotlineReport4",
	] satisfies (keyof typeof translations)[];

	const subreportsMap: Partial<Record<(typeof reports)[number], (keyof typeof translations)[]>> = {
		complianceHotlineReport4: [
			"complianceHotlineReport4-1",
			"complianceHotlineReport4-2",
			"complianceHotlineReport4-3",
			"complianceHotlineReport4-4",
			"complianceHotlineReport4-5",
			"complianceHotlineReport4-6",
			"complianceHotlineReport4-7",
			"complianceHotlineReport4-8",
			"complianceHotlineReport4-9",
			"complianceHotlineReport4-10",
			"complianceHotlineReport4-11",
			"complianceHotlineReport4-12",
			"complianceHotlineReport4-13",
			"complianceHotlineReport4-14",
		],
	};

	setContext(thumbnailImageContextKey, complianceThumbnail);
	/* eslint-disable svelte/no-at-html-tags */
</script>

<Layout
	footerImage={carFooterBanner}
	menuTextColor="text-secondary"
	largeFooterImage
	footerImageClass="max-lg:object-[80%_center]"
>
	<HeroBanner
		backgroundImage={compliance}
		backgroundImageMobile={complianceMd}
		backgroundImageClass="opacity-60 h-full"
		class="min-h-screen overflow-visible"
		hideShowMore
		overlay="black"
		title={translate("compliance")}
	>
		<div class="grid w-full max-w-6xl grid-cols-1 gap-5 text-white md:grid-flow-col md:grid-cols-2 md:grid-rows-2">
			{#each complianceHeaders as header}
				{@const parentheses = parenthesesMap[header]}
				<div
					class="lg:text-2lg flex items-end bg-[#3A3A3A]/50 px-10 py-3.5 text-[1.75rem] font-bold backdrop-blur-2xl lg:px-16"
				>
					<div class="flex flex-wrap items-center gap-x-2 gap-y-0.5">
						<span>{translate(header)}</span>
						{#if parentheses}
							<span class="lg:text-2lg text-[1.25rem]">
								{translate(parentheses)}
							</span>
						{/if}
					</div>
				</div>
			{/each}
		</div>
	</HeroBanner>

	<div class="container">
		<section class="min-h-screen py-28 lg:flex lg:gap-36 lg:py-48">
			<div class="flex basis-2/3 flex-col gap-8 lg:gap-40">
				<h2 class="ml-0 text-[2.75rem] font-semibold lg:text-4xl">{translate("complianceHeader")}</h2>
				<p class="lg:text-2lg mb-24 text-[2rem] lg:mb-0">{translate("compliancePerex")}</p>
			</div>
			<p class="basis-1/3 text-[1.125rem] font-light lg:text-[1.1875rem]">{translate("complianceStandards")}</p>
		</section>

		<section class="flex min-h-screen flex-col py-28 lg:max-w-[60%] lg:py-48">
			<h2 class="mb-20 ml-0 text-[2.75rem] font-semibold lg:text-4xl">{translate("codeOfEthics")}</h2>
			<p class="lg:text-2md text-[1.125rem]">{translate("codeOfEthicsText")}</p>
			<p class="lg:text-2md text-[1.125rem]">
				<span class="font-bold">{translate("codeOfEthicsMotto")}</span>
				{translate("codeOfEthicsMottoText")}
			</p>
		</section>

		<section
			class="flex min-h-screen grid-flow-col grid-cols-[2fr,1fr] grid-rows-[auto,auto,auto,auto] flex-col gap-12 py-28 lg:grid lg:gap-40 lg:py-48"
		>
			<h2 class="row-span-2 m-0 text-[2.75rem] font-semibold lg:text-4xl">
				{`${translate("complianceHotline")} ${translate("complianceHotlineParentheses")}`}
			</h2>
			<div class="row-span-2">
				<p class="lg:text-2md text-[1.125rem] font-light">{translate("complianceHotlinePerex")}</p>
				<p class="lg:text-2lg my-16 text-[1.75rem] font-bold">{translate("complianceHotlineReportingLine")}</p>
				<div>
					<h3 class="lg:text-2lg mb-8 text-[1.75rem] font-bold">
						{translate("complianceHotlineWhatCanBeReported")}
					</h3>
					<p class="lg:text-2md mb-8 text-[1.125rem] font-light">{translate("complianceHotlineReportText")}</p>
					<ul class="lg:text-2md text-[1.125rem] font-light lg:list-disc lg:pl-10">
						{#each reports as report}
							{@const subreports = subreportsMap[report]}
							<li>{translate(report)}</li>
							{#if subreports}
								<li class="list-none">
									<ul class="lg:list-disc lg:pl-10">
										{#each subreports as subreport}
											<li>{translate(subreport)}</li>
										{/each}
									</ul>
								</li>
							{/if}
						{/each}
					</ul>
				</div>
			</div>
			<div class="row-span-3 leading-snug lg:row-start-2">
				<h3 class="lg:text-2lg mb-8 text-[1.75rem] lg:mb-16">{translate("complianceHotlineHowToReport")}</h3>
				<p class="text-[1.125rem] font-light lg:text-[1.1875rem]">{translate("complianceHotlineHereForYou")}</p>
				<ul class="text-[1.125rem] font-light lg:list-disc lg:pl-6 lg:text-[1.1875rem]">
					<li>{translate("complianceHotlineInPerson")}</li>
					<li>{translate("complianceHotlineByPhone")}</li>
					<li>{translate("complianceHotlineByEmail")}</li>
				</ul>

				<h4 class="my-8 text-[1.125rem] font-bold lg:text-[1.1875rem]">
					{translate("complianceHotlineWhistleblowerProtection")}
				</h4>
				<p class="text-[1.125rem] font-light lg:text-[1.1875rem]">
					{translate("complianceHotlineWhistleblowerProtectionText1")}
				</p>
				<p class="text-[1.125rem] font-light lg:text-[1.1875rem]">
					{translate("complianceHotlineWhistleblowerProtectionText2")}
				</p>
				<p class="text-[1.125rem] font-light lg:text-[1.1875rem]">
					{translate("complianceHotlineWhistleblowerProtectionText3")}
					<a class="font-bold" href={translate("complianceHotlineWhistleblowerProtectionText3Link")}>
						{translate("complianceHotlineWhistleblowerProtectionText3Link")}
					</a>
				</p>
				<p class="text-[1.125rem] font-light lg:text-[1.1875rem]">
					{translate("complianceHotlineWhistleblowerProtectionText4")}
					<a class="font-bold" href={translate("complianceHotlineWhistleblowerProtectionText4Link")}>
						{translate("complianceHotlineWhistleblowerProtectionText4Link")}
					</a>
				</p>

				<h4 class="my-8 text-[1.125rem] font-bold lg:text-[1.1875rem]">
					{translate("complianceHotlinePubliclyAvailableInformation")}
				</h4>
				<p class="text-[1.125rem] font-light lg:text-[1.1875rem]">
					{translate("complianceHotlinePubliclyAvailableInformationText")}
					<a href="http://{translate('complianceHotlinePubliclyAvailableInformationLink')}" target="_blank">
						{translate("complianceHotlinePubliclyAvailableInformationLink")}
					</a>
				</p>
			</div>
		</section>

		<!--		<section class="min-h-screen py-28 lg:max-w-[60%] lg:py-48">-->
		<!--			<h2 class="mb-20 ml-0 text-[2.75rem] font-semibold lg:text-4xl">{translate("gdpr")}</h2>-->
		<!--		</section>-->

		<section class="min-h-screen py-28 lg:py-48">
			<h2 class="mb-20 ml-0 text-[2.75rem] font-semibold lg:mb-10 lg:text-4xl">
				{translate("cookiesHeading")}
			</h2>
			<div
				class="[&_h4]:lg:text-2md [&_th]:!font-PlusJakarta [&_td]:!font-PlusJakarta w-full overflow-x-auto py-8 font-light [&_h4]:mt-12 [&_h4]:text-[1.125rem] [&_table]:min-w-[38.125rem] [&_td]:text-xs [&_td]:lg:text-sm [&_th]:min-w-[7.8125rem]"
			>
				{@html `<script type="text/javascript" charset="UTF-8" data-cookiescriptreport="report" src="https://report.cookie-script.com/r/${COOKIES}.js"></script>`}
			</div>
		</section>

		<section class="min-h-screen py-28 lg:max-w-[60%] lg:py-48">
			<h2 class="mb-20 ml-0 text-[2.75rem] font-semibold lg:text-4xl">{translate("internalRegulations")}</h2>

			<div class="flex flex-col gap-8">
				<p class="lg:text-2md text-[1.125rem] font-light">
					{translate("complianceHotlineInternalRegulationsCommitted")}
				</p>

				<ul class="lg:text-2md text-[1.125rem] font-light lg:list-disc lg:pl-10">
					<li>{translate("complianceHotlinePrinciples")}</li>
					<li>{translate("complianceHotlineAntiCorruption")}</li>
					<li>{translate("complianceHotlineCompetitionRules")}</li>
					<li>{translate("complianceHotlineTrade")}</li>
					<li>{translate("complianceHotlineRequirements")}</li>
				</ul>

				<p class="lg:text-2md text-[1.125rem] font-light">
					{translate("complianceHotlineTraining")}
				</p>
			</div>

			<h3 class="lg:text-2lg mb-8 mt-12 text-[1.75rem]">
				{translate("complianceHotlineSuppliers")}
			</h3>
			<p class="lg:text-2md mb-0 text-[1.125rem] font-light">
				{translate("complianceHotlineSuppliersText1")}
			</p>
			<p class="lg:text-2md text-[1.125rem] font-light">
				{translate("complianceHotlineSuppliersText2")}
			</p>
		</section>
	</div>
</Layout>
